/**
 * User and HR Management React Query Hooks
 * 
 * Provides React Query hooks for user management and HR operations including:
 * - User CRUD operations
 * - Department management
 * - User profile management
 * 
 * All hooks use React Query for automatic caching, background refetching,
 * and optimistic updates with proper cache invalidation.
 * 
 * @module hooks/useUsers
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { API_ENDPOINTS } from '../config/api.config.js';

/**
 * Fetches users with optional filtering
 * 
 * @param {Object} filters - Query parameters for filtering users
 * @param {string} [filters.role] - Filter by user role
 * @param {string} [filters.department] - Filter by department
 * @param {string} [filters.status] - Filter by user status
 * @param {string} [filters.search] - Search by name or email
 * @returns {Object} React Query result with users data
 * @property {Object} data - Users response object
 * @property {Array} data.items - Array of user records
 * @property {number} data.total - Total number of users
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useUsers = (filters = {}) => {
  return useQuery({
    queryKey: ['users', filters],
    queryFn: async () => {
      const response = await api.get('/api/v1/users', { params: filters });
      return response.data;
    },
  });
};

/**
 * Fetches a single user by ID
 * 
 * Query is disabled if userId is not provided
 * 
 * @param {string} userId - User ID
 * @returns {Object} React Query result with user data
 * @property {Object} data - User record with full details
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useUser = (userId) => {
  return useQuery({
    queryKey: ['users', userId],
    queryFn: async () => {
      const response = await api.get(`/api/v1/users/${userId}`);
      return response.data;
    },
    enabled: !!userId,
  });
};

/**
 * Mutation hook to create a new user
 * 
 * Automatically invalidates users query on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const createUser = useCreateUser();
 * createUser.mutate({ 
 *   username: 'jdoe', 
 *   email: 'jdoe@example.com',
 *   role: 'operator',
 *   department: 'warehouse' 
 * });
 */
export const useCreateUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/v1/users', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

/**
 * Mutation hook to update an existing user
 * 
 * Automatically invalidates both the specific user and users list on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const updateUser = useUpdateUser();
 * updateUser.mutate({ 
 *   userId: '123', 
 *   data: { status: 'active', role: 'manager' } 
 * });
 */
export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ userId, data }) => {
      const response = await api.put(`/api/v1/users/${userId}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users', variables.userId] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

/**
 * Mutation hook to delete a user
 * 
 * Automatically invalidates users query on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 */
export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (userId) => {
      const response = await api.delete(`${API_ENDPOINTS.USERS}/${userId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users', 'dashboard'] });
    }
  });
};

/**
 * Hook to get user dashboard data
 * @returns {Object} Query result with user dashboard data
 */
export const useUserDashboard = () => {
  return useQuery({
    queryKey: ['users', 'dashboard'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.USERS}/dashboard`);
      const data = response.data || {};
      
      // Role colors for PieChart
      const roleColors = {
        'admin': '#1976d2',
        'Administrator': '#1976d2',
        'manager': '#388e3c',
        'Manager': '#388e3c',
        'operator': '#f57c00',
        'Operator': '#f57c00',
        'client': '#9c27b0',
        'Client': '#9c27b0',
        'Customer': '#7b1fa2',
        'user': '#757575'
      };
      
      // Transform users_by_role to include 'value' and 'name' for PieChart compatibility
      const usersByRole = Array.isArray(data.users_by_role) 
        ? data.users_by_role.map((item, index) => ({
            name: item.role || item.name || 'Unknown',
            value: item.count || item.value || 0,
            count: item.count || item.value || 0,
            color: roleColors[item.role] || roleColors[item.name] || ['#1976d2', '#388e3c', '#f57c00', '#9c27b0', '#7b1fa2'][index % 5]
          }))
        : [];
      
      // Transform recent_activity to include expected fields
      const recentActivity = Array.isArray(data.recent_activity)
        ? data.recent_activity.map(item => ({
            user: item.username || item.user || 'Unknown',
            action: item.activity_type || item.action || 'Activity',
            time: item.activity_time ? new Date(item.activity_time).toLocaleString() : 'Unknown',
            status: item.activity_type === 'login' ? 'success' : 'info'
          }))
        : [];
      
      // Transform user_activity for AreaChart
      const userActivity = Array.isArray(data.user_activity)
        ? data.user_activity.map(item => ({
            month: item.date || item.month || 'Unknown',
            active: item.count || item.active || 0,
            inactive: item.inactive || 0
          }))
        : [];
      
      return {
        totalUsers: data.total_users || 0,
        activeUsers: data.active_users || 0,
        inactiveUsers: data.inactive_users || 0,
        newUsersThisMonth: data.new_users_this_month || 0,
        usersByRole,
        userActivity,
        recentActivity
      };
    },
  });
};

/**
 * Fetches departments for HR management
 * 
 * @returns {Object} React Query result with departments data
 * @property {Array} data - Array of department records
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useDepartments = () => {
  return useQuery({
    queryKey: ['hr', 'departments'],
    queryFn: async () => {
      const response = await api.get('/api/v1/hr/departments');
      return response.data;
    },
  });
};

/**
 * Fetches current user profile
 * 
 * @returns {Object} React Query result with user profile data
 * @property {Object} data - Current user profile
 * @property {string} data.id - User ID
 * @property {string} data.username - Username
 * @property {string} data.email - Email address
 * @property {Array} data.roles - User roles
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useUserProfile = () => {
  return useQuery({
    queryKey: ['auth', 'profile'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.AUTH}/profile`);
      return response.data;
    },
  });
};

/**
 * Mutation hook to update current user profile
 * 
 * Automatically invalidates profile query on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const updateProfile = useUpdateProfile();
 * updateProfile.mutate({ 
 *   email: 'newemail@example.com',
 *   preferences: { language: 'fr-FR' } 
 * });
 */
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.put(`${API_ENDPOINTS.AUTH}/profile`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth', 'profile'] });
    },
  });
};

/**
 * Hook to get available roles
 * @returns {Object} Query result with roles data
 */
export const useRoles = () => {
  return useQuery({
    queryKey: ['auth', 'roles'],
    queryFn: async () => {
      const response = await api.get(`/api/v1/auth/roles`);
      return response.data;
    },
  });
};

/**
 * Hook to get HR metrics
 * @returns {Object} Query result with HR metrics
 */
export const useHRMetrics = () => {
  return useQuery({
    queryKey: ['hr', 'metrics'],
    queryFn: async () => {
      // Use /api/v1/hr/metrics endpoint for workforce metrics
      const { data } = await api.get('/api/v1/hr/metrics');
      return data;
    },
  });
};

/**
 * Hook to get HR operators
 * @returns {Object} Query result with operators data
 */
export const useHROperators = () => {
  return useQuery({
    queryKey: ['hr', 'operators'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.HR}/operators`);
      const data = response.data || {};
      return Array.isArray(data.operators) ? data.operators : [];
    },
  });
};

/**
 * Hook to get HR performance data
 * @returns {Object} Query result with performance data
 */
export const useHRPerformance = () => {
  return useQuery({
    queryKey: ['hr', 'performance'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.HR}/performance`);
      const data = response.data || {};
      return Array.isArray(data.performance) ? data.performance : [];
    },
  });
};

/**
 * Hook to get HR workload forecast
 * @returns {Object} Query result with forecast data
 */
export const useHRForecast = () => {
  return useQuery({
    queryKey: ['hr', 'forecast'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.HR}/forecast`);
      const data = response.data || {};
      return {
        forecast: Array.isArray(data.forecast) ? data.forecast : [],
        recommendations: Array.isArray(data.recommendations) ? data.recommendations : []
      };
    },
  });
};

/**
 * Hook to get HR skills distribution
 * @returns {Object} Query result with skills distribution data
 */
export const useHRSkillsDistribution = () => {
  return useQuery({
    queryKey: ['hr', 'skills-distribution'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.HR}/skills-distribution`);
      const data = response.data || {};
      return Array.isArray(data.distribution) ? data.distribution : [];
    },
  });
};

/**
 * Mutation hook to update user status
 * 
 * Automatically invalidates users queries on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const updateUserStatus = useUpdateUserStatus();
 * updateUserStatus.mutate({ 
 *   userId: '123', 
 *   status: 'active' 
 * });
 */
export const useUpdateUserStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ userId, status }) => {
      const response = await api.patch(`${API_ENDPOINTS.USERS}/${userId}/status`, { status });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users', variables.userId] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users', 'dashboard'] });
    },
  });
};

/**
 * Hook to get HR system status
 * @returns {Object} Query result with HR system status
 */
export const useHRStatus = () => {
  return useQuery({
    queryKey: ['hr', 'status'],
    queryFn: async () => {
      const response = await api.get('/api/v1/hr/status');
      return response.data;
    },
    refetchInterval: 10000, // Auto-refresh every 10 seconds
  });
};

/**
 * Hook to get HR employees
 * @returns {Object} Query result with employees data
 */
export const useHREmployees = () => {
  return useQuery({
    queryKey: ['hr', 'employees'],
    queryFn: async () => {
      const response = await api.get('/api/v1/hr/employees');
      const data = response.data;
      return Array.isArray(data.employees) ? data.employees : [];
    },
  });
};

/**
 * Hook to get HR schedules
 * @returns {Object} Query result with schedules data
 */
export const useHRSchedules = () => {
  return useQuery({
    queryKey: ['hr', 'schedules'],
    queryFn: async () => {
      const response = await api.get('/api/v1/hr/schedules');
      const data = response.data;
      return Array.isArray(data.schedules) ? data.schedules : [];
    },
  });
};

/**
 * Hook to get HR predictions
 * @returns {Object} Query result with predictions data
 */
export const useHRPredictions = () => {
  return useQuery({
    queryKey: ['hr', 'predictions'],
    queryFn: async () => {
      const response = await api.get('/api/v1/hr/predictions');
      const data = response.data;
      return Array.isArray(data.predictions) ? data.predictions : [];
    },
  });
};

/**
 * Hook to get HR analytics
 * @returns {Object} Query result with analytics data
 */
export const useHRAnalytics = () => {
  return useQuery({
    queryKey: ['hr', 'analytics'],
    queryFn: async () => {
      const response = await api.get('/api/v1/hr/analytics');
      return response.data;
    },
    refetchInterval: 10000, // Auto-refresh every 10 seconds
  });
};

/**
 * Query hook to fetch HR profiles
 * 
 * Fetches profiles of HR personnel including interim staff, contractors,
 * and temporary workers with their skills, availability, and performance data.
 * 
 * @returns {Object} React Query result with HR profiles
 * @property {Array} data - Array of HR profile objects
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useHRProfiles = () => {
  return useQuery({
    queryKey: ['hr', 'profiles'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.HR}/profiles`);
      const data = response.data || {};
      return Array.isArray(data) ? data : (Array.isArray(data.items) ? data.items : []);
    },
  });
};

/**
 * Mutation hook to optimize HR schedule
 * 
 * Triggers AI-powered schedule optimization to improve resource allocation,
 * reduce costs, and balance workload across teams. Automatically invalidates
 * HR schedules and analytics queries on success.
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {Function} mutateAsync - Async version of mutate
 * @property {boolean} isPending - Mutation pending state
 * @property {Object} data - Optimization results including improvements
 * 
 * @example
 * const optimizeSchedule = useOptimizeSchedule();
 * optimizeSchedule.mutate();
 * // or
 * const result = await optimizeSchedule.mutateAsync();
 * console.log(result.improvements);
 */
export const useOptimizeSchedule = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      const response = await api.post('/api/v1/hr/optimize-schedule');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'schedules'] });
      queryClient.invalidateQueries({ queryKey: ['hr', 'analytics'] });
      queryClient.invalidateQueries({ queryKey: ['hr', 'predictions'] });
    },
  });
};
